import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiCalendar, FiChevronDown, FiX, FiCheck } from 'react-icons/fi';
import dayjs from 'helpers/dayjs';
import PropTypes from 'prop-types';
import Button from './Button';

// `fullPeriods` controls how the "this period" presets end:
//  - false (default, reports/dashboards): period-to-date — ends today, because
//    you can't report on data that hasn't happened yet.
//  - true (forward-looking filters like the shipment ETD plan): the whole
//    period — ends at endOf(week/month/quarter/year) so future-dated rows
//    (e.g. shipments planned later this year) stay inside the range.
const buildPresets = (fullPeriods = false) => {
  const today = dayjs();
  const periodEnd = (unit) =>
    (fullPeriods ? today.clone().endOf(unit) : today.clone()).format('YYYY-MM-DD');

  return [
    { key: 'today',        label: 'Today',        start: today.clone().format('YYYY-MM-DD'), end: today.clone().format('YYYY-MM-DD') },
    { key: 'yesterday',    label: 'Yesterday',    start: today.clone().subtract(1,'day').format('YYYY-MM-DD'), end: today.clone().subtract(1,'day').format('YYYY-MM-DD') },
    { key: 'this_week',    label: 'This Week',    start: today.clone().startOf('isoWeek').format('YYYY-MM-DD'), end: periodEnd('isoWeek') },
    { key: 'last_week',    label: 'Last Week',    start: today.clone().subtract(1,'week').startOf('isoWeek').format('YYYY-MM-DD'), end: today.clone().subtract(1,'week').endOf('isoWeek').format('YYYY-MM-DD') },
    { key: 'this_month',   label: 'This Month',   start: today.clone().startOf('month').format('YYYY-MM-DD'), end: periodEnd('month') },
    { key: 'last_month',   label: 'Last Month',   start: today.clone().subtract(1,'month').startOf('month').format('YYYY-MM-DD'), end: today.clone().subtract(1,'month').endOf('month').format('YYYY-MM-DD') },
    { key: 'this_quarter', label: 'This Quarter', start: today.clone().startOf('quarter').format('YYYY-MM-DD'), end: periodEnd('quarter') },
    { key: 'last_quarter', label: 'Last Quarter', start: today.clone().subtract(1,'quarter').startOf('quarter').format('YYYY-MM-DD'), end: today.clone().subtract(1,'quarter').endOf('quarter').format('YYYY-MM-DD') },
    { key: 'this_year',    label: 'This Year',    start: today.clone().startOf('year').format('YYYY-MM-DD'), end: periodEnd('year') },
    { key: 'last_year',    label: 'Last Year',    start: today.clone().subtract(1,'year').startOf('year').format('YYYY-MM-DD'), end: today.clone().subtract(1,'year').endOf('year').format('YYYY-MM-DD') },
  ];
};

const formatDisplay = (start, end) => {
  if (!start && !end) return null;
  const fmt = (d) => dayjs(d).format('MMM D, YYYY');
  if (start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
};

const detectPreset = (start, end, presets) =>
  presets.find(p => p.start === start && p.end === end)?.key || null;

/**
 * ReportDateRangePicker – reusable Tailwind date range picker for reports & dashboards.
 * Props: startDate, endDate (YYYY-MM-DD), onChange({ startDate, endDate }), className
 */
const ReportDateRangePicker = ({ startDate, endDate, onChange, className, fullPeriods = false, align = 'left' }) => {
  const presets    = useMemo(() => buildPresets(fullPeriods), [fullPeriods]);
  const [open, setOpen]         = useState(false);
  const [tempStart, setTempStart] = useState(startDate || '');
  const [tempEnd,   setTempEnd]   = useState(endDate   || '');
  const [activePreset, setActivePreset] = useState(() => detectPreset(startDate, endDate, presets));
  const wrapperRef = useRef(null);

  useEffect(() => {
    setTempStart(startDate || '');
    setTempEnd(endDate   || '');
    setActivePreset(detectPreset(startDate, endDate, presets));
  }, [startDate, endDate, presets]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectPreset = (preset) => {
    setTempStart(preset.start);
    setTempEnd(preset.end);
    setActivePreset(preset.key);
  };

  const handleApply = () => {
    if (!tempStart || !tempEnd) return;
    onChange({ startDate: tempStart, endDate: tempEnd });
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setTempStart(''); setTempEnd(''); setActivePreset(null);
    onChange({ startDate: '', endDate: '' });
    setOpen(false);
  };

  const handleCustomStart = (e) => { setTempStart(e.target.value); setActivePreset(null); };
  const handleCustomEnd   = (e) => { setTempEnd(e.target.value);   setActivePreset(null); };

  const activePresetObj = presets.find(p => p.key === activePreset);
  const displayLabel    = formatDisplay(startDate, endDate);
  const isValid         = tempStart && tempEnd && tempStart <= tempEnd;

  return (
    <div ref={wrapperRef} className={`tw-relative tw-inline-block ${className || ''}`}>
      {/* ── Trigger ── */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`tw-inline-flex tw-items-center tw-gap-2 tw-h-9 tw-px-3.5 tw-rounded tw-text-sm tw-font-medium tw-transition-all tw-duration-150 tw-whitespace-nowrap tw-border ${
          open
            ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600 tw-shadow-sm'
            : 'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-border-blue-400 hover:tw-bg-blue-50 hover:tw-text-blue-700'
        }`}
      >
        <FiCalendar size={14} className={open ? 'tw-text-white' : 'tw-text-blue-500'} />

        {/* Preset tag or date label */}
        {activePresetObj && startDate ? (
          <>
            <span className={`tw-font-semibold ${open ? 'tw-text-white' : 'tw-text-gray-900'}`}>
              {activePresetObj.label}
            </span>
            <span className={`tw-text-xs ${open ? 'tw-text-blue-200' : 'tw-text-gray-400'}`}>
              {formatDisplay(startDate, endDate)}
            </span>
          </>
        ) : displayLabel ? (
          <span className={`tw-font-medium ${open ? 'tw-text-white' : 'tw-text-gray-800'}`}>
            {displayLabel}
          </span>
        ) : (
          <span className="tw-text-gray-400">Select date range</span>
        )}

        {/* Clear × */}
        {displayLabel && !open && (
          <span
            onClick={handleClear}
            className="tw-w-4 tw-h-4 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-gray-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-transition-colors tw-ml-0.5"
          >
            <FiX size={11} />
          </span>
        )}

        <FiChevronDown
          size={13}
          className={`tw-transition-transform tw-ml-auto ${open ? 'tw-rotate-180 tw-text-white' : 'tw-text-gray-400'}`}
        />
      </button>

      {/* ── Dropdown Panel ── */}
      {open && (
        <div
          className={`tw-absolute tw-top-full tw-mt-2 ${align === 'right' ? 'tw-right-0' : 'tw-left-0'} tw-z-50 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-2xl tw-overflow-hidden`}
          style={{ minWidth: '460px' }}
        >
          <div className="tw-flex">
            {/* Presets column */}
            <div className="tw-w-40 tw-bg-gray-50 tw-border-r tw-border-gray-100 tw-p-3 tw-flex-shrink-0">
              <p className="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-widest tw-mb-2 tw-px-1">
                Quick Select
              </p>
              <div className="tw-space-y-0.5">
                {presets.map(preset => (
                  <Button
                    key={preset.key}
                    variant='ghost'
                    onClick={() => selectPreset(preset)}
                    className={`tw-w-full tw-text-left tw-px-3 tw-py-2 tw-rounded tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-between tw-gap-1 ${
                      activePreset === preset.key
                        ? 'tw-bg-blue-600 tw-text-blue-300'
                        : 'tw-text-blue-600 hover:tw-bg-blue-50 hover:tw-text-blue-700 hover:tw-shadow-sm'
                    }`}
                  >
                    <span>{preset.label}</span>
                    {activePreset === preset.key && <FiCheck size={12} className="tw-flex-shrink-0" />}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom range panel */}
            <div className="tw-flex-1 tw-p-4 tw-flex tw-flex-col tw-gap-3">
              <p className="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-widest">
                Custom Range
              </p>

              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <div>
                  <label className="tw-block tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1">Start Date</label>
                  <input
                    type="date"
                    value={tempStart}
                    max={tempEnd || undefined}
                    onChange={handleCustomStart}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-bg-gray-50 tw-text-gray-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-bg-white"
                  />
                </div>
                <div>
                  <label className="tw-block tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1">End Date</label>
                  <input
                    type="date"
                    value={tempEnd}
                    min={tempStart || undefined}
                    onChange={handleCustomEnd}
                    className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded tw-text-sm tw-bg-gray-50 tw-text-gray-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-bg-white"
                  />
                </div>
              </div>

              {/* Preview chip */}
              {(tempStart || tempEnd) && (
                <div className="tw-px-3 tw-py-2 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded">
                  <p className="tw-text-[10px] tw-font-bold tw-text-blue-500 tw-uppercase tw-tracking-wider tw-mb-0.5">
                    {activePresetObj ? activePresetObj.label : 'Custom'}
                  </p>
                  <p className="tw-text-sm tw-font-semibold tw-text-blue-800">
                    {formatDisplay(tempStart, tempEnd) || '—'}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="tw-flex tw-gap-2 tw-mt-auto tw-pt-2 tw-border-t tw-border-gray-100">
                <Button
                  variant="danger"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={!isValid}
                  className="tw-w-full"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ReportDateRangePicker.propTypes = {
  startDate: PropTypes.string,
  endDate:   PropTypes.string,
  onChange:  PropTypes.func.isRequired,
  className: PropTypes.string,
  fullPeriods: PropTypes.bool,
  // 'right' opens the panel toward the left — for a trigger near the right edge.
  align: PropTypes.oneOf(['left', 'right'])
};
ReportDateRangePicker.defaultProps = { startDate: '', endDate: '', className: '', fullPeriods: false };

export default ReportDateRangePicker;
